// Quote Store
// Zustand store for Quote state management

import { create } from 'zustand'
import type {
  Product,
  SelectedProduct,
  QuoteDetails,
  QuoteCalculations,
  PlanType,
  CurrencyType,
  DiscountType,
  UpliftType,
  PartnershipDetails,
  PartnershipTerms,
  ImplementationScope,
  LicensePlan,
  LicensePlanType,
} from './types'
import { PLAN_PRICING } from './data'
import { calculateQuote } from './utils/calculateQuote'

interface QuoteState {
  // Quote details
  details: QuoteDetails

  // Products
  products: Product[]
  selectedProducts: SelectedProduct[]
  productSearch: string
  productsLoaded: boolean

  // Calculations
  calculations: QuoteCalculations | null

  // Partnership/Reseller
  partnership: PartnershipDetails

  // Read-only locks. `all` is true while the deal is `in_review` OR awaiting
  // implementation review, so the numbers a reviewer / impl team sees in DealHub
  // match the snapshot. `implementationFee` locks the impl-fee field alone after
  // the Impl team has injected a value (the 'reviewed' state), so the submitter
  // can't clobber it before continuing to standard review.
  quoteLocks: { all: boolean; implementationFee: boolean }

  // Actions - Details
  setCustomerName: (name: string) => void
  setCountry: (country: string) => void
  setPreparedBy: (name: string) => void
  setStartDate: (date: string) => void
  setCustomerPlan: (plan: PlanType | '') => void
  setPlatformFees: (fees: number) => void
  setImplementationFee: (fee: number) => void
  // Apply the scope's implementation fee and record its provenance + snapshot.
  applyScopeImplementationFee: (scopeHours: number, fee: number) => void
  setChildAccounts: (count: number) => void
  setContractTermMonths: (months: number) => void
  setPaymentFrequency: (freq: QuoteDetails['paymentFrequency'] | '') => void
  setAutoRenew: (autoRenew: boolean) => void
  setPartnerCommission: (commission: number) => void
  setPortalSeats: (seats: number) => void
  setSupportLevel: (level: number) => void
  setImplementationSupport: (support: number) => void
  setHostedOneSDK: (hosted: number) => void
  setMinimumCommitment: (commitment: number) => void
  setDisplayCurrency: (currency: CurrencyType | '') => void
  setHourlyRate: (rate: number) => void
  setScopeSourceDealId: (dealId: number | null) => void

  // Actions - Products
  setProducts: (products: Product[]) => void
  setProductSearch: (search: string) => void
  setProductsLoaded: (loaded: boolean) => void
  addProduct: (product: Product) => void
  addProducts: (products: Product[]) => void
  removeProduct: (uniqueId: number) => void
  clearAllProducts: () => void
  updateProductVolume: (uniqueId: number, volume: number) => void
  updateProductDiscount: (uniqueId: number, type: DiscountType, value?: number) => void
  updateProductUplift: (uniqueId: number, type: UpliftType, value?: number) => void
  updateProductCostPrice: (uniqueId: number, costPrice: number) => void

  // Actions - Lock
  setQuoteLocks: (locks: { all: boolean; implementationFee: boolean }) => void

  // Actions - Calculations
  calculate: () => void
  resetQuote: () => void
  getQuoteState: () => Partial<QuoteState>
  loadQuoteState: (state: Partial<QuoteState>) => void

  // Actions - Partnership
  setIsReseller: (isReseller: boolean) => void
  setPartnerName: (name: string) => void
  setProposalYear: (year: number) => void
  setPartnershipTerms: (terms: Partial<PartnershipTerms>) => void
  setImplementationScope: (scope: Partial<ImplementationScope>) => void
  setLicensePlan: (planType: LicensePlanType) => void
  setLicensePlanPrice: (price: number) => void
  setAnnualLicenseFee: (fee: number) => void
  setAnnualMinimumCommitment: (commitment: number) => void
  setImplementationBand: (band: string) => void
  setImplementationDays: (days: number) => void
  setBlendedResourceRate: (rate: number) => void
  setPartnershipNotes: (notes: string[]) => void
  addPartnershipNote: (note: string) => void
  removePartnershipNote: (index: number) => void
}

const LICENSE_PLANS: Record<LicensePlanType, LicensePlan> = {
  BASE: {
    type: 'BASE',
    price: 3300,
    features: {
      rulesets: 1,
      portalSeats: 1,
      uatSandboxAccounts: 1,
      childAccounts: 1,
      evidenceCapture: true,
      apiAccess: true,
      basicHostedOneSDK: true,
    },
  },
  PRO: {
    type: 'PRO',
    price: 6000,
    features: {
      rulesets: 3,
      portalSeats: 5,
      uatSandboxAccounts: 2,
      childAccounts: 3,
      evidenceCapture: true,
      apiAccess: true,
      basicHostedOneSDK: true,
    },
  },
  ENTERPRISE: {
    type: 'ENTERPRISE',
    price: 12000,
    features: {
      rulesets: 10,
      portalSeats: 10,
      uatSandboxAccounts: 5,
      childAccounts: 10,
      evidenceCapture: true,
      apiAccess: true,
      basicHostedOneSDK: true,
    },
  },
}

const getDefaultPartnership = (): PartnershipDetails => ({
  isReseller: false,
  partnerName: '',
  proposalYear: new Date().getFullYear(),
  terms: {
    implementationFeeBilling: '' as PartnershipTerms['implementationFeeBilling'],
    licenceFeeBilling: '' as PartnershipTerms['licenceFeeBilling'],
    minimumCommitmentBilling: '' as PartnershipTerms['minimumCommitmentBilling'],
    transactionFeeBilling: '' as PartnershipTerms['transactionFeeBilling'],
    paymentTermsDays: 0 as PartnershipTerms['paymentTermsDays'],
  },
  implementationScope: {
    frankieOnePortal: false,
    sdkRecommendations: false,
    apiIntegrationSupport: false,
    smeKycIdvKybRecipes: false,
    standardRecipesWorkflows: false,
    serviceAdviceSelection: false,
    childAccountClone: false,
    childAccountCloneCount: 0,
    hyperCareMonitoring: false,
    hyperCareDays: 0,
    hyperCareWeeks: 0,
    portalTraining: false,
  },
  licensePlan: { ...LICENSE_PLANS.BASE, price: 0 },
  annualLicenseFee: 0,
  annualMinimumCommitment: 0,
  implementationBand: '',
  implementationDays: 0,
  blendedResourceRate: 0,
  notes: [],
})

const getDefaultDetails = (): QuoteDetails => ({
  customerName: '',
  country: '',
  preparedBy: '',
  startDate: '',
  customerPlan: '',
  platformFees: 0,
  implementationFee: 0,
  implementationFeeFromScope: false,
  implementationScopeHours: null,
  implementationScopeAppliedAt: null,
  scopeSourceDealId: null,
  childAccounts: 0,
  contractTermMonths: 0,
  paymentFrequency: '',
  autoRenew: true,
  partnerCommission: 0,
  portalSeats: 0,
  supportLevel: 0,
  implementationSupport: 0,
  hostedOneSDK: 0,
  minimumCommitment: 0,
  displayCurrency: '',
  hourlyRate: 0,
})

// Helper: Get price for plan tier
const getPriceForPlan = (product: Product, plan: PlanType | ''): number => {
  switch (plan) {
    case 'Basic':
      return product.basePrice ?? 0
    case 'Professional':
      return product.proPrice ?? 0
    case 'Enterprise':
      return product.enterprisePrice ?? 0
    default:
      return product.enterprisePrice ?? 0
  }
}

// Helper: Calculate effective price applying either a discount OR an uplift
// (mutually exclusive — enforced by the store actions). Both adjustments are
// computed against the plan price, so a "10% uplift" on a €100 line is +€10
// regardless of any concurrent discount.
const calculateEffectivePrice = (
  planPrice: number,
  discountType: DiscountType,
  discountValue: number,
  upliftType: UpliftType,
  upliftValue: number
): number => {
  let p = planPrice
  if (discountType === 'absolute') {
    p -= discountValue
  } else if (discountType === 'percent') {
    p -= (planPrice * discountValue) / 100
  }
  if (upliftType === 'absolute') {
    p += upliftValue
  } else if (upliftType === 'percent') {
    p += (planPrice * upliftValue) / 100
  }
  return Math.max(0, p)
}

export const useQuoteStore = create<QuoteState>((set, get) => ({
  details: getDefaultDetails(),
  products: [],
  selectedProducts: [],
  productSearch: '',
  productsLoaded: false,
  calculations: null,
  partnership: getDefaultPartnership(),
  quoteLocks: { all: false, implementationFee: false },

  // Read-only locks — Quote.tsx wires these to dealStatus + impl_review_status
  // on load and on review actions. Child components disable inputs on these.
  setQuoteLocks: (locks) => set({ quoteLocks: locks }),

  // Details setters
  setCustomerName: (name) => {
    set((state) => ({ details: { ...state.details, customerName: name } }))
    get().calculate()
  },

  setCountry: (country) => {
    set((state) => ({ details: { ...state.details, country } }))
  },

  setPreparedBy: (name) => {
    set((state) => ({ details: { ...state.details, preparedBy: name } }))
  },

  setStartDate: (date) => {
    set((state) => ({ details: { ...state.details, startDate: date } }))
  },

  setCustomerPlan: (plan) => {
    if (!plan) {
      set((state) => ({
        details: {
          ...state.details,
          customerPlan: plan,
          platformFees: 0,
          implementationFee: 0,
          minimumCommitment: 0,
        },
      }))
      get().calculate()
      return
    }

    const pricing = PLAN_PRICING[plan]
    set((state) => {
      // Update selected products with new plan prices
      const updatedProducts = state.selectedProducts.map((p) => {
        const newPlanPrice = getPriceForPlan(p, plan)
        const newEffectivePrice = calculateEffectivePrice(
          newPlanPrice,
          p.discountType,
          p.discountValue,
          p.upliftType,
          p.upliftValue
        )
        return {
          ...p,
          planPrice: newPlanPrice,
          effectivePrice: newEffectivePrice,
        }
      })

      return {
        details: {
          ...state.details,
          customerPlan: plan,
          platformFees: pricing.platformFees,
          implementationFee: pricing.implementationFee,
          minimumCommitment: pricing.minimumCommitment,
        },
        selectedProducts: updatedProducts,
      }
    })
    get().calculate()
  },

  setPlatformFees: (fees) => {
    set((state) => ({ details: { ...state.details, platformFees: fees } }))
    get().calculate()
  },

  setImplementationFee: (fee) => {
    // Manual edit — the fee is no longer sourced from the scope, so clear the
    // provenance + snapshot. (The scope-apply path uses applyScopeImplementationFee.)
    set((state) => ({
      details: {
        ...state.details,
        implementationFee: fee,
        implementationFeeFromScope: false,
        implementationScopeHours: null,
        implementationScopeAppliedAt: null,
      },
    }))
    get().calculate()
  },

  applyScopeImplementationFee: (scopeHours, fee) => {
    set((state) => ({
      details: {
        ...state.details,
        implementationFee: fee,
        implementationFeeFromScope: true,
        implementationScopeHours: scopeHours,
        implementationScopeAppliedAt: new Date().toISOString(),
      },
    }))
    get().calculate()
  },

  setChildAccounts: (count) => {
    set((state) => ({ details: { ...state.details, childAccounts: count } }))
    get().calculate()
  },

  setContractTermMonths: (months) => {
    set((state) => ({ details: { ...state.details, contractTermMonths: months } }))
    get().calculate()
  },

  setPaymentFrequency: (freq) => {
    set((state) => ({ details: { ...state.details, paymentFrequency: freq } }))
  },

  setAutoRenew: (autoRenew) => {
    set((state) => ({ details: { ...state.details, autoRenew } }))
  },

  setPartnerCommission: (commission) => {
    set((state) => ({ details: { ...state.details, partnerCommission: commission } }))
    get().calculate()
  },

  setPortalSeats: (seats) => {
    set((state) => ({ details: { ...state.details, portalSeats: seats } }))
    get().calculate()
  },

  setSupportLevel: (level) => {
    set((state) => ({ details: { ...state.details, supportLevel: level } }))
    get().calculate()
  },

  setImplementationSupport: (support) => {
    set((state) => ({ details: { ...state.details, implementationSupport: support } }))
    get().calculate()
  },

  setHostedOneSDK: (hosted) => {
    set((state) => ({ details: { ...state.details, hostedOneSDK: hosted } }))
    get().calculate()
  },

  setMinimumCommitment: (commitment) => {
    set((state) => ({ details: { ...state.details, minimumCommitment: commitment } }))
    get().calculate()
  },

  setDisplayCurrency: (currency) => {
    set((state) => ({ details: { ...state.details, displayCurrency: currency } }))
    get().calculate()
  },

  setHourlyRate: (rate) => {
    set((state) => ({ details: { ...state.details, hourlyRate: rate } }))
    get().calculate()
  },

  setScopeSourceDealId: (dealId) => {
    set((state) => ({ details: { ...state.details, scopeSourceDealId: dealId } }))
  },

  // Product actions
  setProducts: (products) => set({ products }),

  setProductSearch: (search) => set({ productSearch: search }),

  setProductsLoaded: (loaded) => set({ productsLoaded: loaded }),

  addProduct: (product) => {
    const { details } = get()
    const planPrice = getPriceForPlan(product, details.customerPlan)

    const selectedProduct: SelectedProduct = {
      ...product,
      uniqueId: Date.now() + Math.random(),
      volume: 0,
      planPrice,
      discountType: 'none',
      discountValue: 0,
      upliftType: 'none',
      upliftValue: 0,
      effectivePrice: planPrice,
      customCostPrice: null,
    }

    set((state) => ({
      selectedProducts: [...state.selectedProducts, selectedProduct],
    }))
    get().calculate()
  },

  addProducts: (products) => {
    const { details, selectedProducts } = get()
    const existingIds = new Set(selectedProducts.map((p) => p.id))

    const newProducts = products
      .filter((p) => !existingIds.has(p.id))
      .map((product) => {
        const planPrice = getPriceForPlan(product, details.customerPlan)
        return {
          ...product,
          uniqueId: Date.now() + Math.random(),
          volume: 0,
          planPrice,
          discountType: 'none' as DiscountType,
          discountValue: 0,
          upliftType: 'none' as UpliftType,
          upliftValue: 0,
          effectivePrice: planPrice,
          customCostPrice: null,
        }
      })

    set((state) => ({
      selectedProducts: [...state.selectedProducts, ...newProducts],
    }))
    get().calculate()
  },

  removeProduct: (uniqueId) => {
    set((state) => ({
      selectedProducts: state.selectedProducts.filter((p) => p.uniqueId !== uniqueId),
    }))
    get().calculate()
  },

  clearAllProducts: () => {
    set({ selectedProducts: [] })
    get().calculate()
  },

  updateProductVolume: (uniqueId, volume) => {
    set((state) => ({
      selectedProducts: state.selectedProducts.map((p) =>
        p.uniqueId === uniqueId ? { ...p, volume } : p
      ),
    }))
    get().calculate()
  },

  updateProductDiscount: (uniqueId, type, value = 0) => {
    set((state) => ({
      selectedProducts: state.selectedProducts.map((p) => {
        if (p.uniqueId !== uniqueId) return p

        const discountValue = type === 'none' ? 0 : value
        // Mutex: turning a discount ON clears any uplift on this line.
        // Turning it OFF (type='none') leaves the uplift alone — the user
        // may have neither active.
        const upliftType: UpliftType = type === 'none' ? p.upliftType : 'none'
        const upliftValue = type === 'none' ? p.upliftValue : 0
        const effectivePrice = calculateEffectivePrice(
          p.planPrice,
          type,
          discountValue,
          upliftType,
          upliftValue
        )

        return {
          ...p,
          discountType: type,
          discountValue,
          upliftType,
          upliftValue,
          effectivePrice,
        }
      }),
    }))
    get().calculate()
  },

  updateProductUplift: (uniqueId, type, value = 0) => {
    set((state) => ({
      selectedProducts: state.selectedProducts.map((p) => {
        if (p.uniqueId !== uniqueId) return p

        const upliftValue = type === 'none' ? 0 : value
        // Mutex: turning an uplift ON clears any discount on this line.
        const discountType: DiscountType = type === 'none' ? p.discountType : 'none'
        const discountValue = type === 'none' ? p.discountValue : 0
        const effectivePrice = calculateEffectivePrice(
          p.planPrice,
          discountType,
          discountValue,
          type,
          upliftValue
        )

        return {
          ...p,
          discountType,
          discountValue,
          upliftType: type,
          upliftValue,
          effectivePrice,
        }
      }),
    }))
    get().calculate()
  },

  updateProductCostPrice: (uniqueId, costPrice) => {
    set((state) => ({
      selectedProducts: state.selectedProducts.map((p) =>
        p.uniqueId === uniqueId ? { ...p, customCostPrice: costPrice } : p
      ),
    }))
    get().calculate()
  },

  // Calculation
  calculate: () => {
    const { details, selectedProducts } = get()
    // Delegates to the shared pure calc (src/features/quote/utils/calculateQuote.ts) so the
    // exact same maths runs server-side in the review-queue enrichment.
    set({ calculations: calculateQuote(details, selectedProducts) })
  },

  resetQuote: () => {
    set({
      details: getDefaultDetails(),
      selectedProducts: [],
      calculations: null,
      partnership: getDefaultPartnership(),
    })
  },

  getQuoteState: () => {
    const { details, selectedProducts, partnership } = get()
    return { details, selectedProducts, partnership }
  },

  loadQuoteState: (state) => {
    if (state.details) {
      set({ details: state.details })
    }
    if (state.selectedProducts) {
      // Defensive normalisation: legacy quotes were saved before the uplift
      // fields existed, and one historical quirk is using a negative
      // `discountValue` as an "uplift workaround". Migrate those lines to
      // the proper uplift shape on load so the UI renders cleanly and the
      // calculator no longer relies on the negative-discount math trick.
      const normalised = state.selectedProducts.map((p) => {
        const upliftType: UpliftType = ((p as { upliftType?: UpliftType }).upliftType) ?? 'none'
        const upliftValue: number = ((p as { upliftValue?: number }).upliftValue) ?? 0

        // Legacy negative-discount → uplift conversion, only when this line
        // hasn't already been migrated.
        if (upliftType === 'none' && p.discountType !== 'none' && p.discountValue < 0) {
          return {
            ...p,
            discountType: 'none' as DiscountType,
            discountValue: 0,
            upliftType: p.discountType,
            upliftValue: -p.discountValue,
          }
        }
        return { ...p, upliftType, upliftValue }
      })
      set({ selectedProducts: normalised })
    }
    if (state.partnership) {
      set({ partnership: state.partnership })
    }
    get().calculate()
  },

  // Partnership actions
  setIsReseller: (isReseller) => {
    set((state) => ({ partnership: { ...state.partnership, isReseller } }))
  },

  setPartnerName: (name) => {
    set((state) => ({ partnership: { ...state.partnership, partnerName: name } }))
  },

  setProposalYear: (year) => {
    set((state) => ({ partnership: { ...state.partnership, proposalYear: year } }))
  },

  setPartnershipTerms: (terms) => {
    set((state) => ({
      partnership: {
        ...state.partnership,
        terms: { ...state.partnership.terms, ...terms },
      },
    }))
  },

  setImplementationScope: (scope) => {
    set((state) => ({
      partnership: {
        ...state.partnership,
        implementationScope: { ...state.partnership.implementationScope, ...scope },
      },
    }))
  },

  setLicensePlan: (planType) => {
    const plan = LICENSE_PLANS[planType]
    set((state) => ({
      partnership: {
        ...state.partnership,
        licensePlan: plan,
        annualLicenseFee: plan.price,
      },
    }))
  },

  setLicensePlanPrice: (price) => {
    set((state) => ({
      partnership: {
        ...state.partnership,
        licensePlan: { ...state.partnership.licensePlan, price },
        annualLicenseFee: price,
      },
    }))
  },

  setAnnualLicenseFee: (fee) => {
    set((state) => ({ partnership: { ...state.partnership, annualLicenseFee: fee } }))
  },

  setAnnualMinimumCommitment: (commitment) => {
    set((state) => ({ partnership: { ...state.partnership, annualMinimumCommitment: commitment } }))
  },

  setImplementationBand: (band) => {
    set((state) => ({ partnership: { ...state.partnership, implementationBand: band } }))
  },

  setImplementationDays: (days) => {
    set((state) => ({ partnership: { ...state.partnership, implementationDays: days } }))
  },

  setBlendedResourceRate: (rate) => {
    set((state) => ({ partnership: { ...state.partnership, blendedResourceRate: rate } }))
  },

  setPartnershipNotes: (notes) => {
    set((state) => ({ partnership: { ...state.partnership, notes } }))
  },

  addPartnershipNote: (note) => {
    set((state) => ({
      partnership: { ...state.partnership, notes: [...state.partnership.notes, note] },
    }))
  },

  removePartnershipNote: (index) => {
    set((state) => ({
      partnership: {
        ...state.partnership,
        notes: state.partnership.notes.filter((_, i) => i !== index),
      },
    }))
  },
}))
